import type { PropsWithChildren, FunctionComponent } from 'react'
import { useContext, useEffect, useMemo, useState, useCallback } from 'react'
import {
  type NotificationsContextValue,
  withNotificationsContext,
} from '../../../../../contexts/notifications/NotificationsContext.ts'
import type { RetailerRouteComponentProps } from '../../../../../components/RetailerRoute.tsx'
import type { ProductFormValues } from '../../../types/product.types.ts'
import type { Item } from '../../../types/items.types.ts'
import FilterBar from '../../../../../components/FilterBar.tsx'
// eslint-disable-next-line workspaces/no-relative-imports
import { useDomainMessages } from '../../../../../../../../../domains/products/src/utils/domain/intl'
import { useDashMessages } from '../../../../../../intl/intl.hooks'
import { FormattedMessage } from 'react-intl'
import RegionLocationDropdown from '../../../../../components/RegionLocationDropdown.tsx'
import { type ProductLocationFilters, ProductLocationsFilters } from './ProductLocationsFilters.tsx'
import { LoadingDots, spacing } from '@retailer-platform/shared-components'
import { Button, Alert, SelectField, Text } from '@retailer-platform/shared-components/src/tds'
import { WarehouseContext } from '../../../../../../utils/contexts/warehouse/WarehouseContext.ts'
import AddProductToLocations from './AddProductToLocations.tsx'
import BulkEditProductLocations from './BulkEditProductLocationsModal.tsx'
// eslint-disable-next-line workspaces/no-relative-imports
import { Container } from '../../../../../../../../../domains/catalog-admin/src/utils/common.styles.tsx'
import instacart from '../../../../../common/instacart.ts'
import deprecatedAnalytics from '../../../../../common/deprecatedAnalytics.tsx'
import ApiResponseError from '../../../../../common/errors/ApiResponseError.ts'
import { each, eq } from 'lodash'
import styled from '@emotion/styled'
import { CountryId } from '../../../../../common/utils/countries/countries.types.ts'
import { withTrackEventOnMount } from '../../../../../../utils/events/hocs.tsx'
import withRetailerRouter from '../../../../../common/withRouter.tsx'
import {
  ProductLocationsTableColumns,
  ProductLocationsTableColumnSortEnum,
  type ProductLocationsTableColumnSortState,
} from './ProductLocationsTableColumns.tsx'
import { ProductLocationsTableRow } from './ProductLocationsTableRow.tsx'
import { ErrorPage } from '../../../../../../gin-and-tonic/containers/error-page/ErrorPage.tsx'
import { RegionLocationContext } from '../../../../../contexts/regionLocation/RegionLocationContext.ts'
import { withProductLocationsHelper } from './ProductLocationsHelper'
import withSurfaceContext from './withSurfaceContext'
import { SurfaceContext } from '../../../../../contexts/surface/SurfaceContext'
import useAccessControl from '../../../../../components/AccessControl/useAccessControl'
import AddTempOverridePatchModal from './AddTempOverridePatchModal'
import { useRetailerId } from '../../../../../../utils/routing/params.hooks'
import {
  useGetAvailabilityScoreOverrideQuery,
  useHasAvailabilityScoreOverride,
  useUpdateAvailabilityScoreOverrideMutation,
} from '@retailer-platform/domain-products/availabilityScoreOverride'
import { notifyUpdateErrors } from './components/AvailabilityScoreOverrideErrors'
import { Permission } from '../../../../../common/types.ts'
import { AccessGated } from '../../../../../../exports/access-control.ts'
import type { PaginationParams } from './ProductLocationsHelper'
import { cleanItemDraftsPayload } from './ProductLocations.utils'

const ProductLocationFilterBar = styled(FilterBar)({
  justifyContent: 'left',
  gap: spacing.X8,
  paddingTop: spacing.X16,
  minHeight: 'auto',
  borderBottom: 'none',
})

const ProductLocationFilterBarItem = styled.div()

const Spacing = styled.div({
  flex: 1,
})

const PAGINATION_OPTIONS = [10, 20, 50, 100] as const

export const countryIdToDomain: { [key in string]: string } = {
  [CountryId.CANADA]: 'https://instacart.ca',
  [CountryId.USA]: 'https://instacart.com',
}

export const NINETY_DAYS = 1000 * 60 * 60 * 24 * 90

export interface Query {
  item_ids: string
  inventory_area_ids: string
  region_ids: string
}

interface Props extends NotificationsContextValue, RetailerRouteComponentProps<{}, Query> {
  partnerId: string
  warehouseId: string
  productId: string
  product?: ProductFormValues
  className?: string
  // All following props come from withProductLocationsHelper
  data?: Item[]
  isLoading?: boolean
  isError?: boolean
  refetch?: (() => void) | ((paginationParams?: PaginationParams) => void)
  itemIds?: number[]
  // Pagination props from withProductLocationsHelper
  paginationData?: {
    currentPage: number
    pageSize: number
    totalCount: number
    totalPages: number
  }
}

interface State {
  selected: number[]
  currentRowEditIndex: number
  itemDrafts: {
    [index: string]: string
  }
  activeModal: 'addLocations' | 'bulkEditLocations' | 'addPatch' | null
  filters: ProductLocationFilters
  sortState: ProductLocationsTableColumnSortState
  availabilityByInventoryAreaId: { [index: string]: boolean }
}

interface AvailabilityScoreOverrideState {
  enable: boolean
  startAt: Date
  endAt: Date
}

const initialAvailabilityScoreOverrideState: AvailabilityScoreOverrideState = {
  enable: null,
  startAt: null,
  endAt: null,
}

const ProductLocations: FunctionComponent<PropsWithChildren<Props>> = ({
  className,
  data,
  warehouseId,
  partnerId,
  productId,
  isLoading,
  isError,
  itemIds,
  refetch,
  notify,
  notifyError,
  query,
  paginationData,
}) => {
  const { inventoryAreas } = useContext(RegionLocationContext)
  const [state, setState] = useState<State>({
    selected: [],
    currentRowEditIndex: -1,
    itemDrafts: {},
    activeModal: null,
    filters: {
      hasSalePricing: false,
      isAvailable: false,
      hasAvailabilityScoreOverride: false,
    },
    availabilityByInventoryAreaId: {},
    sortState: {
      column: 'inventory_area',
      order: ProductLocationsTableColumnSortEnum.ASC,
    },
  })
  const { availabilityByInventoryAreaId, sortState, activeModal, selected, filters } = state
  const { surfaceId, setSurfaceId } = useContext(SurfaceContext)

  const hasAccess = useAccessControl()
  const sproutsEnabled = hasAccess([
    {
      // @ts-expect-error, throwaway feature toggle
      warehouseFeatureToggles: ['rt_catalog_sprouts_improved_product_search'],
    },
  ])
  // The following feature toggle is currently being used by costco to override availability for products and use the surface selector
  // https://instacart.atlassian.net/browse/CAT-61326
  // TODO: Rename this feature toggle to something more descriptive
  const temporaryAvailabilityOverrideEnabled = hasAccess([
    {
      // @ts-expect-error, throwaway feature toggle
      warehouseFeatureToggles: ['rt_catalog_temporary_availability_overrides'],
    },
  ])
  const internalAdminTempOverrideEnabled = hasAccess([
    {
      // @ts-expect-error, throwaway feature toggle
      warehouseFeatureToggles: ['rt_catalog_internal_admin_temp_override_enabled'],
    },
  ])
  const [availabilityScoreOverrideState, setAvailabilityScoreOverrideState] =
    useState<AvailabilityScoreOverrideState>(initialAvailabilityScoreOverrideState)

  const showAvailabilityScoreOverride = useHasAvailabilityScoreOverride(warehouseId)

  const {
    data: availabilityScoreOverrideMapData,
    loading: availabilityScoreOverrideLoading,
    error: availabilityScoreOverrideError,
  } = showAvailabilityScoreOverride
    ? useGetAvailabilityScoreOverrideQuery({
        variables: {
          entityIds: [
            {
              retailerProductId: {
                retailerId: warehouseId,
                productId: productId,
              },
            },
          ],
          retailerId: partnerId,
        },
      })
    : {
        data: null,
        loading: false,
        error: null,
      }

  const availabilityScoreOverrideMap =
    availabilityScoreOverrideMapData?.retailerProductAttributeEditServiceGetAvailabilityScoreOverride?.entities?.reduce(
      (dict, data) => {
        dict[parseInt(data.entityId.itemId.itemId)] = data.isEnabled
        return dict
      },
      {}
    ) ?? {}

  const [updateAvailabilityScoreOverrideMutation] = useUpdateAvailabilityScoreOverrideMutation()

  const retailerId = useRetailerId()

  useEffect(() => {
    const availabilityByInventoryAreaId = (data ?? []).reduce((previousValue, currentValue) => {
      const storeName = currentValue?.inventory_area_id
      return {
        ...previousValue,
        [storeName]: true,
      }
    }, {})

    setState(state => ({
      ...state,
      availabilityByInventoryAreaId: availabilityByInventoryAreaId,
    }))
  }, [data])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const storedPartialFailure = window.sessionStorage.getItem('tempOverridePartialFailure')
    if (!storedPartialFailure) return

    window.sessionStorage.removeItem('tempOverridePartialFailure')
    try {
      const { successCount, failedCount, totalCount } = JSON.parse(storedPartialFailure)
      notifyError(
        <FormattedMessage
          id="productsDomain.view.storeLocations.override.modal.partial.failure"
          values={{ successCount, failedCount, totalCount }}
        />
      )
    } catch (error) {
      console.error('Failed to parse temp override partial failure message', error)
    }
  }, [notifyError])

  const filteredAndSortedItems = useMemo(() => {
    const doFiltering =
      filters.isAvailable || filters.hasSalePricing || filters.hasAvailabilityScoreOverride

    let newItems = data ?? []
    if (doFiltering) {
      newItems = (data ?? []).filter(data => {
        let isValid = false
        if (filters.isAvailable) isValid = data.available
        if (filters.hasSalePricing) isValid = data.sale_price_active
        if (filters.hasAvailabilityScoreOverride) {
          isValid =
            availabilityScoreOverrideError != null || availabilityScoreOverrideMap[data.id] == true
        }
        return isValid
      })
    }

    if (sortState) {
      newItems = newItems.sort((itemA, itemB) => {
        const attributeA = JSON.stringify(itemA?.[sortState.column] ?? '')
        const attributeB = JSON.stringify(itemB?.[sortState.column] ?? '')

        return sortState.order === ProductLocationsTableColumnSortEnum.ASC
          ? attributeA.localeCompare(attributeB, undefined, { numeric: true })
          : attributeB.localeCompare(attributeA, undefined, { numeric: true })
      })
    }

    return newItems
  }, [
    data,
    filters.hasSalePricing,
    filters.isAvailable,
    filters.hasAvailabilityScoreOverride,
    sortState,
    availabilityScoreOverrideError,
    availabilityScoreOverrideMap,
  ])

  // Use the memoized filtered and sorted items
  const items = filteredAndSortedItems

  const takeDownItems = useMemo(
    () => (items ? items : []).filter(i => i.emergency_take_down).map(i => i.id),
    [items]
  )

  const anyTakeDown = useMemo(() => takeDownItems.length > 0, [takeDownItems])
  const takeDownTime = useMemo(
    () => (items ? items : []).filter(i => i.emergency_take_down)[0]?.emergency_take_down_end,
    [items]
  )

  const handleEditRowClick = (currentRowEditIndex: number) => {
    setState(state => ({
      ...state,
      currentRowEditIndex:
        currentRowEditIndex === state.currentRowEditIndex ? -1 : currentRowEditIndex,
      itemDrafts: {},
    }))
    setAvailabilityScoreOverrideState(initialAvailabilityScoreOverrideState)
  }

  const inventoryAreaMap = useMemo(() => {
    return (inventoryAreas ?? []).reduce((currentMap, inventoryArea) => {
      return {
        [inventoryArea.id]: inventoryArea,
        ...currentMap,
      }
    }, {})
  }, [inventoryAreas])

  const availableItems = useMemo(() => {
    return items?.reduce((a, i) => {
      a.push({
        itemId: i.id,
        displayName: inventoryAreaMap[i.inventory_area_id]
          ? inventoryAreaMap[i.inventory_area_id].warehouse_name ??
            (inventoryAreaMap[i.inventory_area_id].name ||
              inventoryAreaMap[i.inventory_area_id].street)
          : i.inventory_area.name,
      })
      return a
    }, [])
  }, [items, inventoryAreaMap])

  const itemLocaleCode = useMemo(() => items?.[0]?.locale_code, [items])

  const handleEdit = (attr: string, value: string) => {
    setState(state => ({
      ...state,
      itemDrafts: {
        ...state.itemDrafts,
        [attr]: value,
      },
    }))
  }

  const handleModalClose = (success?: boolean) => {
    if (success === true && activeModal === 'addPatch') {
      notify(<FormattedMessage id="productsDomain.view.storeLocations.modal.success" />)
    }
    setState(state => ({
      ...state,
      activeModal: null,
    }))
  }

  const handleConfirmEditClick = async () => {
    const editedItem = items[state.currentRowEditIndex]
    try {
      if (showAvailabilityScoreOverride && availabilityScoreOverrideState.enable != null) {
        const {
          enable: availabilityScoreOverride,
          startAt: availabilityScoreOverrideStartAt,
          endAt: availabilityScoreOverrideEndAt,
        } = availabilityScoreOverrideState

        if (
          availabilityScoreOverride &&
          (availabilityScoreOverrideStartAt == null || availabilityScoreOverrideEndAt == null)
        ) {
          notifyError(
            <FormattedMessage
              id={
                'catalog.products.locations.requestSubmitted.availabilityScoreOverride.missingDateError'
              }
            />
          )
          return
        }

        const data = {
          entityId: [
            {
              itemId: {
                itemId: editedItem.id.toString(),
              },
            },
          ],
          retailerId: warehouseId,
          enable: availabilityScoreOverride,
          overrideStartAt: availabilityScoreOverride ? availabilityScoreOverrideStartAt : null,
          overrideEndAt: availabilityScoreOverride ? availabilityScoreOverrideEndAt : null,
        }
        const resp = await updateAvailabilityScoreOverrideMutation({
          variables: {
            data,
          },
        })

        const errors =
          resp.data?.retailerProductAttributeEditServiceUpdateAvailabilityScoreOverride?.errors
        if (errors) {
          errors.forEach(notifyUpdateErrors)
          return
        }
      }

      // Clean up the payload - remove undefined/null/empty values and ensure cost_unit is valid
      const cleanedPayload = cleanItemDraftsPayload(state.itemDrafts)

      let item: Item
      try {
        item = await instacart.go.put(
          `/v1/partners/${partnerId}/warehouses/${warehouseId}/products/${productId}/items/${editedItem.id}`,
          { data: cleanedPayload }
        )
      } catch (putError) {
        // Re-throw with cleanedPayload in scope for error logging
        console.error('PUT request failed. Payload was:', cleanedPayload)
        throw putError
      }
      each(state.itemDrafts, (attribute, value) => {
        deprecatedAnalytics.track('product.warehouse_location_change', {
          source_type: attribute,
          source_value: value,
          item_id: item.id,
        })
      })

      notify(<FormattedMessage id="catalog.products.locations.requestSubmitted" />)

      if (typeof refetch === 'function') {
        refetch()
      }
    } catch (e) {
      if (e instanceof ApiResponseError && e.errors?.length) {
        notifyError(<>{e.errors[0]}</>)
      } else {
        notifyError(<FormattedMessage id="catalog.products.locations.error.generic" />)
      }
    }
  }

  const onSelectItem = (item_id: number) => {
    setState(state => {
      const selected = [...state.selected]

      if (selected.includes(item_id)) {
        const i = selected.indexOf(item_id)
        selected.splice(i, 1)
      } else {
        selected.push(item_id)
      }

      return { ...state, selected }
    })
  }

  const handleSelectAllClick = () => {
    setState(state => ({
      ...state,
      itemDrafts: {},
      currentRowEditIndex: -1,
      selected: areAllItemsSelected() ? [] : [...itemIds],
    }))
  }

  const areAllItemsSelected = () => {
    if (!itemIds?.length) return false

    return eq(itemIds.sort().toString(), selected.sort().toString())
  }

  const handleBulkEditClick = () => {
    setState(state => ({
      ...state,
      activeModal: 'bulkEditLocations',
    }))
  }

  const handleAddLocationClick = () => {
    setState({
      ...state,
      activeModal: 'addLocations',
    })
  }

  // TODO Remove after rivercat is fast.
  const handleAddPatchClick = () => {
    setState({
      ...state,
      activeModal: 'addPatch',
    })
  }

  const i18nMessages = useDashMessages({
    paginationAll: 'common.pagination.all',
    tooManyLocations: 'productLocations.error.tooManySelected',
  })
  // If we have too many locations selected, then the request might time out
  // In this case, we want to prompt them to select a specific location.
  // If they have a specific location selected and they still get an error, we just
  // want to show them the default error message.
  const customErrorMessage =
    isError && !query.region_ids && !query.inventory_area_ids
      ? i18nMessages.tooManyLocations
      : undefined

  const i18n = useDomainMessages({
    marketplace: 'productsDomain.view.details.surfaces.marketplace',
    storefrontPro: 'productsDomain.view.details.surfaces.storefrontPro',
  })

  return (
    <Container className={className}>
      <ProductLocationFilterBar>
        <ProductLocationFilterBarItem>
          <RegionLocationDropdown
            isDisabled={selected.length > 0}
            useInventoryAreas
            hideTestNamesInInventoryAreas={sproutsEnabled}
            availabilityByInventoryAreaId={availabilityByInventoryAreaId}
          />
        </ProductLocationFilterBarItem>
        {(sproutsEnabled || temporaryAvailabilityOverrideEnabled) && (
          <ProductLocationFilterBarItem>
            <SelectField
              options={[
                {
                  label: i18n.marketplace,
                  value: '1',
                },
                {
                  label: i18n.storefrontPro,
                  value: '2',
                },
              ]}
              value={surfaceId.toString()}
              onChange={value => setSurfaceId(parseInt(value))}
            />
          </ProductLocationFilterBarItem>
        )}
        <ProductLocationFilterBarItem>
          <ProductLocationsFilters
            filters={state.filters}
            onChange={filters => setState({ ...state, filters })}
            showAvailabilityScoreOverride={showAvailabilityScoreOverride}
          />
        </ProductLocationFilterBarItem>
        <ProductLocationFilterBarItem>
          {selected.length > 0 && (
            <Text typography="bodyRegular">
              <FormattedMessage
                id="catalog.products.locations.items.selected"
                values={{ count: selected.length }}
              />
            </Text>
          )}
        </ProductLocationFilterBarItem>
        <Spacing />
        {(sproutsEnabled ||
          temporaryAvailabilityOverrideEnabled ||
          internalAdminTempOverrideEnabled) && (
          <AccessGated
            accessControlConfig={{ permissions: Permission.PackagesDashboardCatalogProductsModify }}
            accessBlockedResult={'disable'}
          >
            <Button
              disabled={!items?.length}
              onClick={handleAddPatchClick}
              style={{ whiteSpace: 'nowrap' }}
              variant="secondary"
            >
              <FormattedMessage id="catalog.products.locations.sprouts.add-item-availability-patch" />
            </Button>
          </AccessGated>
        )}
        <ProductLocationFilterBarItem>
          {!!selected.length && (
            <AccessGated
              accessControlConfig={{
                permissions: Permission.PackagesDashboardCatalogProductsModify,
              }}
              accessBlockedResult={'disable'}
            >
              <Button onClick={handleBulkEditClick}>
                <FormattedMessage
                  id="catalog.products.locations.bulk-edit"
                  values={{ count: selected.length }}
                />
              </Button>
            </AccessGated>
          )}
          {!selected.length && (
            <AccessGated
              accessControlConfig={{
                permissions: Permission.PackagesDashboardCatalogProductsModify,
              }}
              accessBlockedResult={'disable'}
            >
              <Button onClick={handleAddLocationClick}>
                <FormattedMessage id="catalog.products.locations.add-locations" />
              </Button>
            </AccessGated>
          )}
        </ProductLocationFilterBarItem>
      </ProductLocationFilterBar>
      {anyTakeDown && (
        <Alert variant={'warning'}>
          <FormattedMessage id="productsDomain.view.storeLocations.override.alert" /> {takeDownTime}
        </Alert>
      )}
      <WarehouseContext.Consumer>
        {warehouseContext => {
          if (!warehouseContext) {
            throw new Error('WarehouseContext not provided')
          }
          if (isLoading) return <LoadingDots height={24} centered />
          if (isError) return <ErrorPage>{customErrorMessage}</ErrorPage>

          return (
            <div>
              {/* Table in its own scrollable container */}
              <div style={{ overflow: 'auto', width: '100%' }}>
                <table style={{ width: '100%', border: `none` }}>
                  <thead>
                    <tr>
                      <ProductLocationsTableColumns
                        areAllItemsSelected={areAllItemsSelected}
                        handleSelectAllClick={handleSelectAllClick}
                        sortState={sortState}
                        setSortState={sortState => setState({ ...state, sortState: sortState })}
                        showAvailabilityScoreOverride={showAvailabilityScoreOverride}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <ProductLocationsTableRow
                        key={item.id}
                        availabilityScoreOverride={
                          availabilityScoreOverrideError
                            ? null
                            : availabilityScoreOverrideMap[item.id] == true
                        }
                        availabilityScoreOverrideLoading={availabilityScoreOverrideLoading}
                        inventoryAreaMap={inventoryAreaMap}
                        item={item}
                        index={index}
                        country={warehouseContext?.warehouse?.country}
                        handleConfirmEditClick={handleConfirmEditClick}
                        handleEditRowClick={handleEditRowClick}
                        handleEdit={handleEdit}
                        onSelectItem={onSelectItem}
                        selected={state.selected}
                        currentRowEditIndex={state.currentRowEditIndex}
                        showAvailabilityScoreOverride={showAvailabilityScoreOverride}
                        setAvailabilityScoreOverride={setAvailabilityScoreOverrideState}
                        partnerId={partnerId}
                        warehouseId={warehouseId}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination outside the scrollable container - stays fixed at bottom */}
              {paginationData && paginationData.totalCount > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '16px',
                    borderTop: '1px solid #e0e0e0',
                    backgroundColor: '#fafafa',
                    marginTop: '0', // Remove gap to make it feel more connected
                    borderRadius: '0 0 4px 4px', // Subtle rounded bottom corners
                  }}
                >
                  {/* Items count display */}
                  <Text typography="bodyRegular">
                    {items.length === 0 ? (
                      <FormattedMessage
                        id="catalog.products.locations.pagination.noResults"
                        defaultMessage="No items match the selected filters"
                      />
                    ) : (
                      <FormattedMessage
                        id="catalog.products.locations.pagination.showing"
                        defaultMessage="Showing {from} to {to} of {total} items{filtered}"
                        values={{
                          from: 1,
                          to: items.length,
                          total: items.length,
                          filtered:
                            items.length !== paginationData.totalCount
                              ? ` (filtered from ${paginationData.totalCount})`
                              : '',
                        }}
                      />
                    )}
                  </Text>

                  {/* Page controls - only show when there are items and meaningful pagination */}
                  {items.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      {/* Page size selector - only show when filtering isn't active or there are many items */}
                      {(!filters.isAvailable &&
                        !filters.hasSalePricing &&
                        !filters.hasAvailabilityScoreOverride) ||
                      items.length > 10 ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Text typography="bodyRegular">
                            <FormattedMessage
                              id="catalog.products.locations.pagination.itemsPerPage"
                              defaultMessage="Items per page:"
                            />
                          </Text>
                          <select
                            value={
                              paginationData.pageSize === paginationData.totalCount
                                ? 'all'
                                : paginationData.pageSize
                            }
                            onChange={e => {
                              const { value } = e.target
                              const newPageSize =
                                value === 'all' ? paginationData.totalCount : Number(value)
                              ;(refetch as (paginationParams?: PaginationParams) => void)?.({
                                limit: newPageSize,
                                offset: 0, // Reset to first page when changing page size
                              })
                            }}
                            style={{
                              padding: '4px 8px',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              fontSize: '14px',
                              backgroundColor: '#fff',
                            }}
                          >
                            {PAGINATION_OPTIONS.map(size => (
                              <option key={size} value={size}>
                                {size}
                              </option>
                            ))}
                            <option value="all">{i18nMessages.paginationAll}</option>
                          </select>
                        </div>
                      ) : null}

                      {/* Page navigation - only show when not filtering and multiple pages exist */}
                      {!filters.isAvailable &&
                        !filters.hasSalePricing &&
                        !filters.hasAvailabilityScoreOverride &&
                        paginationData.totalPages > 1 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Button
                              variant="secondary"
                              disabled={paginationData.currentPage === 1}
                              onClick={() => {
                                const newOffset =
                                  (paginationData.currentPage - 2) * paginationData.pageSize
                                ;(refetch as (paginationParams?: PaginationParams) => void)?.({
                                  limit: paginationData.pageSize,
                                  offset: newOffset,
                                })
                              }}
                            >
                              <FormattedMessage
                                id="catalog.products.locations.pagination.previous"
                                defaultMessage="Previous"
                              />
                            </Button>

                            <Text typography="bodyRegular" style={{ padding: '0 8px' }}>
                              <FormattedMessage
                                id="catalog.products.locations.pagination.page"
                                defaultMessage="Page {current} of {total}"
                                values={{
                                  current: paginationData.currentPage,
                                  total: paginationData.totalPages,
                                }}
                              />
                            </Text>

                            <Button
                              variant="secondary"
                              disabled={paginationData.currentPage === paginationData.totalPages}
                              onClick={() => {
                                const newOffset =
                                  paginationData.currentPage * paginationData.pageSize
                                ;(refetch as (paginationParams?: PaginationParams) => void)?.({
                                  limit: paginationData.pageSize,
                                  offset: newOffset,
                                })
                              }}
                            >
                              <FormattedMessage
                                id="catalog.products.locations.pagination.next"
                                defaultMessage="Next"
                              />
                            </Button>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        }}
      </WarehouseContext.Consumer>
      {activeModal === 'addLocations' ? (
        <AddProductToLocations
          partnerId={partnerId}
          warehouseId={warehouseId}
          productId={productId}
          onClose={handleModalClose}
        />
      ) : activeModal === 'bulkEditLocations' ? (
        <BulkEditProductLocations
          partnerId={partnerId}
          warehouseId={warehouseId}
          productId={productId}
          itemIds={selected}
          onClose={handleModalClose}
          showAvailabilityScoreOverride={showAvailabilityScoreOverride}
        />
      ) : activeModal === 'addPatch' ? (
        <WarehouseContext.Consumer>
          {warehouseContext => {
            const localeId =
              warehouseContext?.warehouse?.locales?.find(
                locale => locale.localeCode === itemLocaleCode
              )?.localeId ?? 1
            return (
              <AddTempOverridePatchModal
                onClose={handleModalClose}
                items={availableItems}
                initialItemIds={takeDownItems}
                productId={productId.toString()}
                retailerId={retailerId.toString()}
                surfaceId={surfaceId}
                localeId={Number(localeId)}
                notify={notify}
                notifyError={notifyError}
              />
            )
          }}
        </WarehouseContext.Consumer>
      ) : null}
    </Container>
  )
}

export default withTrackEventOnMount({
  id: 'catalog.products.store_locations.viewed',
  description: 'Viewed the store locations page for a specific product',
})(
  withRetailerRouter(
    withNotificationsContext(withSurfaceContext(withProductLocationsHelper(ProductLocations)))
  )
)
